import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
} from '@mui/lab';
import { Alert, Box, Button, Card, CardHeader, Container, Divider, Grid, Stack, Typography } from '@mui/material';
import { ThreeDots } from 'react-loader-spinner';
import { Page } from '../../../components';
import DetailField from '../../../components/DetailField';
import DetailGrid from '../../../components/DetailGrid';
import Label from '../../../components/label';
import { fDate, fDateTime } from '../../../utils/formatTime';
import { fCurrency } from '../../../utils/formatNumber';
import { useHasRole } from '../../../shared/auth/useProfile';
import { MANAGER_ROLES } from '../../../shared/auth/roles';
import { getRequest } from '../store/workflowActions';
import { DecisionDialog, RATING_COLORS, STATUS_COLORS, TYPE_LABELS, humanize } from '../components';

const APPROVAL_COLORS = { APPROVED: 'success', REJECTED: 'error', PENDING: 'default' };

// Steps the web does on its existing screens, and steps only the app has.
const CONTINUE_ELSEWHERE = {
  RECEIVE: 'Confirm receipt',
  DISPATCH: 'Dispatch is recorded from the mobile app',
  RECORD_REPAIR: 'The technician’s work record is captured on the mobile app',
};

/**
 * One request -- transfer, disposal, maintenance job or regularization -- with
 * its approvals, full timeline, and the decisions open to the signed-in user.
 * Reached from the Inbox, the notification feed, and the "History" links on
 * the transfer, disposal and maintenance screens.
 */
export default function RequestDetail() {
  const { key } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isManager = useHasRole(MANAGER_ROLES);
  const { isLoading, request } = useSelector((state) => state.workflowReducer);
  const [decision, setDecision] = useState(null);

  useEffect(() => {
    dispatch(getRequest(key));
  }, [dispatch, key]);

  const loaded = request && request.id === key;
  if (!loaded) {
    return (
      <Page title="Request">
        <Container maxWidth="xl">
          {isLoading ? (
            <Stack alignItems="center" sx={{ py: 6 }}>
              <ThreeDots height="80" width="80" radius="9" color="#2065d1" visible />
            </Stack>
          ) : (
            <Alert severity="warning">This request does not exist, or you do not have access to it.</Alert>
          )}
        </Container>
      </Page>
    );
  }

  const recordId = request.id.slice(3);
  // Where the underlying record lives on the web, for the steps done there.
  const recordLink = {
    TRANSFER: '/dashboard/transfers',
    DISPOSAL: '/dashboard/disposals',
    REPAIR: isManager ? `/dashboard/maintenance/${recordId}` : `/dashboard/maintenance-info/${recordId}`,
  }[request.type];

  const decisions = request.available_actions.filter((action) => ['APPROVE', 'REJECT'].includes(action.code));
  const elsewhere = request.available_actions.filter((action) => CONTINUE_ELSEWHERE[action.code]);
  const { asset } = request;

  return (
    <Page title={request.reference}>
      {decision && (
        <DecisionDialog
          open
          request={request}
          action={decision}
          onClose={(saved) => {
            setDecision(null);
            // The decision returns the updated request; refetch only to be safe
            // about anything the approval triggered.
            if (saved) dispatch(getRequest(key));
          }}
        />
      )}
      <Container maxWidth="xl">
        <Stack spacing={3}>
          <Card>
            <Box sx={{ p: 3 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
                <Box>
                  <Typography variant="h5">{request.reference}</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Label>{TYPE_LABELS[request.type]}</Label>
                    <Label color={STATUS_COLORS[request.status] || 'default'}>{humanize(request.status)}</Label>
                    {request.source === 'APP' && <Label color="info">Raised on the app</Label>}
                  </Stack>
                  <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                    Raised by {request.initiator_name || '—'}
                    {request.initiator_branch_name ? ` (${request.initiator_branch_name})` : ''} ·{' '}
                    {fDateTime(request.created_at)}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap">
                  {decisions.map((action) => (
                    <Button
                      key={`${action.code}-${action.role}`}
                      size="small"
                      variant={action.code === 'APPROVE' ? 'contained' : 'outlined'}
                      color={action.code === 'APPROVE' ? 'primary' : 'error'}
                      onClick={() => setDecision(action)}
                    >
                      {action.label}
                    </Button>
                  ))}
                  {recordLink && (
                    <Button size="small" variant="outlined" onClick={() => navigate(recordLink)}>
                      Open record
                    </Button>
                  )}
                </Stack>
              </Stack>
              {elsewhere.map((action) => (
                <Alert key={action.code} severity="info" sx={{ mt: 2 }}>
                  Next for you: {action.label}.{' '}
                  {action.code === 'RECEIVE'
                    ? 'Confirm it from the record’s screen (Open record).'
                    : CONTINUE_ELSEWHERE[action.code]}
                </Alert>
              ))}
            </Box>
          </Card>

          {/* A Box, because Stack resets its children's margins and would cancel
              the Grid container's negative margin, pushing the columns right. */}
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={7}>
                <Stack spacing={3}>
                  {asset && (
                    <Card>
                      <CardHeader
                        title="Asset"
                        action={
                          <Button size="small" onClick={() => navigate(`/dashboard/assets/${asset.id}`)}>
                            Open asset
                          </Button>
                        }
                      />
                      <DetailGrid>
                        <DetailField label="Description" value={asset.description} />
                        <DetailField label="Tag" value={asset.tag} />
                        <DetailField label="Branch" value={asset.branch_name} />
                        <DetailField label="Department" value={asset.department_name} />
                        <DetailField label="Custodian" value={asset.custodian_name} />
                        <DetailField label="Net book value" value={fCurrency(asset.nbv)} />
                      </DetailGrid>
                      {asset.rating && <RatingPanel rating={request.rating_snapshot || asset.rating} />}
                    </Card>
                  )}
                  <Card>
                    <CardHeader title="Details" />
                    <RequestDetails request={request} />
                  </Card>
                </Stack>
              </Grid>

              <Grid item xs={12} md={5}>
                <Stack spacing={3}>
                  <Card>
                    <CardHeader title="Approvals" />
                    <Stack divider={<Divider />} sx={{ px: 3, pb: 2 }}>
                      {request.approvals.map((approval) => (
                        <Box key={approval.role} sx={{ py: 1.5 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle2">{approval.role_label}</Typography>
                            <Label color={APPROVAL_COLORS[approval.status]}>{humanize(approval.status)}</Label>
                          </Stack>
                          {approval.approver_name && (
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {approval.approver_name}
                              {approval.acted_at ? ` · ${fDateTime(approval.acted_at)}` : ''}
                              {approval.via === 'WEB' ? ' · administrator approval on the web' : ''}
                            </Typography>
                          )}
                          {approval.comment && <Typography variant="body2">“{approval.comment}”</Typography>}
                          {approval.confirmed_red_rating && (
                            <Typography variant="caption" sx={{ color: 'error.main' }}>
                              Approved despite a RED rating
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </Card>

                  <Card>
                    <CardHeader title="Timeline" />
                    <Timeline sx={{ m: 0, px: 2, [`& .MuiTimelineOppositeContent-root`]: { flex: 0.35 } }}>
                      {request.timeline.map((event, index) => (
                        <TimelineItem key={event.id || `synthesised-${index}`}>
                          <TimelineOppositeContent variant="caption" sx={{ color: 'text.secondary' }}>
                            {fDateTime(event.timestamp)}
                          </TimelineOppositeContent>
                          <TimelineSeparator>
                            <TimelineDot color={index === request.timeline.length - 1 ? 'primary' : 'grey'} />
                            {index < request.timeline.length - 1 && <TimelineConnector />}
                          </TimelineSeparator>
                          <TimelineContent>
                            <Typography variant="subtitle2">{event.action}</Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {[event.actor_name, event.actor_role_label].filter(Boolean).join(' · ')}
                            </Typography>
                            {event.note && <Typography variant="body2">{event.note}</Typography>}
                          </TimelineContent>
                        </TimelineItem>
                      ))}
                    </Timeline>
                  </Card>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </Container>
    </Page>
  );
}

// ---------------------------------------------------------------------------

/* eslint-disable react/prop-types */

function RatingPanel({ rating }) {
  return (
    <Box sx={{ px: 3, pb: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">Repair eligibility</Typography>
        <Label color={RATING_COLORS[rating.level] || 'default'}>{rating.level}</Label>
      </Stack>
      <Stack spacing={0.75}>
        {rating.factors.map((factor) => (
          <Stack key={factor.key} direction="row" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="body2">{factor.label}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {factor.detail}
              </Typography>
            </Box>
            <Label color={RATING_COLORS[factor.level] || 'default'}>{factor.value_label}</Label>
          </Stack>
        ))}
      </Stack>
      {rating.used_replacement_cost && (
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
          NBV is zero or near zero, so replacement cost was used for the cost factor.
        </Typography>
      )}
    </Box>
  );
}

function RequestDetails({ request }) {
  const { transfer, disposal, repair, regularization, receipt } = request;
  return (
    <DetailGrid>
      {transfer && (
        <>
          <DetailField label="From" value={transfer.from_branch_name} />
          <DetailField label="To" value={transfer.to_branch_name} />
          <DetailField label="Receiving department" value={transfer.to_department_name} />
          <DetailField label="Receiving custodian" value={transfer.receiving_custodian_name} />
          <DetailField label="For disposal" value={transfer.for_disposal ? 'Yes (to Kyangombe)' : 'No'} />
          <DetailField label="Dispatched" value={transfer.dispatched_at ? fDateTime(transfer.dispatched_at) : null} />
          <DetailField label="Notes" value={transfer.notes} multiline sx={{ gridColumn: '1 / -1' }} />
        </>
      )}
      {disposal && (
        <>
          <DetailField label="Reason" value={disposal.reason} multiline sx={{ gridColumn: '1 / -1' }} />
          <DetailField label="Method" value={disposal.method} />
          <DetailField label="Location" value={disposal.location_name} />
          <DetailField label="Buyer" value={disposal.customer_name} />
          <DetailField label="Estimate" value={disposal.estimate_amount ? fCurrency(disposal.estimate_amount) : null} />
        </>
      )}
      {repair && (
        <>
          <DetailField label="Fault" value={repair.fault_description} multiline sx={{ gridColumn: '1 / -1' }} />
          <DetailField label="Estimated cost" value={fCurrency(repair.estimated_cost)} />
          <DetailField label="Total cost" value={fCurrency(repair.total_cost)} />
          <DetailField label="Job card" value={repair.job_card_number} />
          <DetailField label="Done by" value={repair.in_house ? 'In-house' : repair.service_provider_name} />
          <DetailField
            label="Sent to technician"
            value={repair.sent_for_repair_at ? fDateTime(repair.sent_for_repair_at) : null}
          />
          {repair.work_record && (
            <>
              <DetailField
                label="Technician"
                value={[repair.work_record.technician_name, repair.work_record.supplier_name]
                  .filter(Boolean)
                  .join(' — ')}
              />
              <DetailField
                label="Work done"
                value={repair.work_record.services_or_parts}
                multiline
                sx={{ gridColumn: '1 / -1' }}
              />
              <DetailField
                label="Work dates"
                value={`${fDate(repair.work_record.work_start_date)} – ${fDate(repair.work_record.work_end_date)}`}
              />
              <DetailField
                label="Attachments"
                value={
                  repair.work_record.attachments.length
                    ? repair.work_record.attachments.map((file) => (
                        <Box key={file.id}>
                          <a href={file.url} target="_blank" rel="noreferrer">
                            {file.name}
                          </a>
                        </Box>
                      ))
                    : null
                }
                multiline
              />
            </>
          )}
        </>
      )}
      {regularization && (
        <>
          <DetailField
            label="Direction"
            value={regularization.direction === 'ADOPT' ? 'Adopt into branch' : 'Release from branch'}
          />
          <DetailField label="Stock take" value={regularization.audit_reference} />
          <DetailField label="Branch" value={regularization.branch_name} />
          <DetailField label="Registered at" value={regularization.registered_branch_name} />
          <DetailField label="Notes" value={regularization.notes} multiline sx={{ gridColumn: '1 / -1' }} />
        </>
      )}
      {receipt && (
        <>
          <DetailField label="Receipt" value={humanize(receipt.decision)} />
          <DetailField
            label="Received by"
            value={[receipt.acted_by_name, receipt.acted_at && fDateTime(receipt.acted_at)].filter(Boolean).join(' · ')}
          />
          {receipt.reason && (
            <DetailField label="Reason" value={receipt.reason} multiline sx={{ gridColumn: '1 / -1' }} />
          )}
        </>
      )}
    </DetailGrid>
  );
}
